/**
 * Typed wrappers around localStorage so feature code never reads/writes raw strings.
 * Keys are centralized here for greppability and to keep the legacy app's storage contract
 * (both apps run side-by-side during migration).
 */

export const STORAGE_KEYS = {
  idToken: 'id_token',
  role: 'role',
  fullName: 'fullName',
  portalOrganizationIds: 'portalOrganizationIds',
  portalOrganizationId: 'portalOrganizationId',
  organizationName: 'organizationName',
  restaurantId: 'restaurantId',
  newOrderTimeLeft: 'newOrderTimeLeft',
  acceptedOrderTimeLeft: 'acceptedOrderTimeLeft',
  readyOrderTimeLeft: 'readyOrderTimeLeft',
  inDeliveryOrderTimeLeft: 'inDeliveryOrderTimeLeft',
  archiveFilters: 'ordersArchiveFilters_v3',
} as const;

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

export const storage = {
  getString(key: StorageKey): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setString(key: StorageKey, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* quota / private-mode — ignore */
    }
  },
  getNumber(key: StorageKey, fallback: number): number {
    const raw = storage.getString(key);
    if (raw === null) return fallback;
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
  },
  setNumber(key: StorageKey, value: number): void {
    storage.setString(key, String(value));
  },
  getJson<T>(key: StorageKey): T | null {
    const raw = storage.getString(key);
    if (raw === null) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  },
  setJson<T>(key: StorageKey, value: T): void {
    try {
      storage.setString(key, JSON.stringify(value));
    } catch {
      /* circular — ignore */
    }
  },
  remove(key: StorageKey): void {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
  clear(): void {
    try {
      localStorage.clear();
    } catch {
      /* ignore */
    }
  },
  /**
   * Remove only the session-bound keys (token, role, name, org ids). Preserves
   * user-tuned UI preferences (timer minutes, archive filter snapshot) across
   * sign-out and 401 redirects so the dispatcher doesn't lose their setup.
   */
  clearSession(): void {
    try {
      localStorage.removeItem(STORAGE_KEYS.idToken);
      localStorage.removeItem(STORAGE_KEYS.role);
      localStorage.removeItem(STORAGE_KEYS.fullName);
      localStorage.removeItem(STORAGE_KEYS.portalOrganizationIds);
      localStorage.removeItem(STORAGE_KEYS.portalOrganizationId);
      localStorage.removeItem(STORAGE_KEYS.organizationName);
      localStorage.removeItem(STORAGE_KEYS.restaurantId);
    } catch {
      /* ignore */
    }
  },
};
